import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, RadialBarChart, RadialBar } from "recharts";
import { Fire, Target, TrendUp, PencilLine, Exam, ArrowRight, Sparkle } from "@phosphor-icons/react";
import { motion } from "framer-motion";

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/dashboard");
                setData(data);
            } catch (e) {
                if (e.response?.status === 401) navigate("/");
            } finally {
                setLoading(false);
            }
        })();
    }, [navigate]);

    if (loading) return <Layout><SkeletonDash /></Layout>;
    if (!data) return <Layout><div className="text-slate-500">Something went wrong.</div></Layout>;

    const { stats, trend, subject_strength, next_action, user, recent_mains } = data;
    const readinessData = [{ name: "readiness", value: stats.readiness, fill: "#1E3A8A" }];

    return (
        <Layout>
            <div className="mb-8">
                <div className="text-xs uppercase tracking-widest text-slate-500">Dashboard</div>
                <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight mt-1 text-slate-900" data-testid="dashboard-title">
                    Namaskāra, {user?.name?.split(" ")[0] || "aspirant"}.
                </h1>
                <p className="text-slate-500 mt-2 text-sm">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
            </div>

            {/* 3 Exam Board Sections + Current Affairs */}
            <div className="mb-10">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-4">Choose your section</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {[
                        { id: "kpsc", name: "KPSC", full: "Karnataka Public Service Commission", desc: "KAS, Gazetted Probationers, FDA / SDA and Group-C recruitment exams.", tint: "from-amber-50 to-white", accent: "text-amber-700", to: "/mcq?board=kpsc" },
                        { id: "ksp", name: "KSP", full: "Karnataka State Police", desc: "Police Sub-Inspector, Constable and other Karnataka State Police recruitments.", tint: "from-indigo-50 to-white", accent: "text-indigo-700", to: "/mcq?board=ksp" },
                        { id: "kea", name: "KEA", full: "Karnataka Examinations Authority", desc: "Common Entrance Test for Engineering, Agriculture, Pharmacy & Medical admissions.", tint: "from-rose-50 to-white", accent: "text-rose-700", to: "/mcq?board=kea" },
                        { id: "current-affairs", name: "Current Affairs", full: "Daily & Weekly Digest", desc: "Karnataka & national current affairs, budget analyses, notifications and monthly digests curated for aspirants.", tint: "from-emerald-50 to-white", accent: "text-emerald-700", to: "/current-affairs" },
                    ].map((b) => (
                        <button
                            key={b.id}
                            data-testid={`board-card-${b.id}`}
                            onClick={() => navigate(b.to)}
                            className={`text-left bento-card bg-gradient-to-br ${b.tint} group`}
                        >
                            <div className={`text-[10px] uppercase tracking-[0.25em] font-semibold ${b.accent}`}>{b.name}</div>
                            <div className="font-heading font-bold text-xl mt-2 text-slate-900 leading-tight">{b.full}</div>
                            <div className="text-sm text-slate-600 mt-3 leading-relaxed">{b.desc}</div>
                            <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#1E3A8A] group-hover:gap-3 transition-all">
                                {b.id === "current-affairs" ? "Read digest" : "Start practice"} <ArrowRight size={16} weight="bold" />
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Subject Strength removed per user request */}
        </Layout>
    );
}

function StatCard({ icon: Icon, label, value, tone }) {
    const tones = { indigo: "text-[#1E3A8A]", emerald: "text-emerald-600", gold: "text-gold-700" };
    return (
        <div className="bento-card">
            <Icon size={22} weight="duotone" className={tones[tone] || "text-slate-700"} />
            <div className="text-xs uppercase tracking-widest text-slate-500 mt-3">{label}</div>
            <div className="font-mono text-3xl font-bold mt-1 text-slate-900">{value}</div>
        </div>
    );
}

function QuickAction({ icon: Icon, title, desc, onClick, testId }) {
    return (
        <button data-testid={testId} onClick={onClick} className="bento-card text-left group hover:border-[#1E3A8A]/40">
            <Icon size={26} weight="duotone" className="text-[#1E3A8A]" />
            <div className="font-heading font-semibold text-lg mt-4 text-slate-900">{title}</div>
            <div className="text-sm text-slate-500 mt-1">{desc}</div>
            <div className="mt-4 inline-flex items-center gap-1 text-xs text-[#1E3A8A] group-hover:gap-2 transition-all">Open <ArrowRight size={14} /></div>
        </button>
    );
}

function EmptyPanel({ label }) {
    return <div className="h-32 grid place-items-center text-sm text-slate-400">{label}</div>;
}

function SkeletonDash() {
    return (
        <div className="space-y-6">
            <div className="h-10 w-64 bg-slate-100 rounded animate-pulse" />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-32 bg-slate-100 rounded-2xl animate-pulse" />
                ))}
            </div>
        </div>
    );
}
